import React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useIntl } from "react-intl";
import { Check } from "lucide-react";

const ProgressStepper = ({ steps, activeStep, labels, onStepClick }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const intl = useIntl();
  const circleSize = isDesktop ? 40 : 32;

  // Mobile: a slim progress bar with "Step x/y · label" — takes less space
  // than the circles and finally tells phone users what's left (labels were
  // desktop-only). Desktop keeps the circle stepper unchanged.
  if (!isDesktop) {
    const total = steps.length;
    const clampedIndex = Math.min(activeStep, total - 1);
    const current = Math.min(activeStep + 1, total);
    const percent = total > 1 ? Math.min(100, (activeStep / (total - 1)) * 100) : 100;
    const labelId = labels?.[steps[clampedIndex]];

    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: "0.375rem" }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontFamily: "'Segoe UI Semibold', system-ui, sans-serif",
              color: "hsl(215.4, 16.3%, 46.9%)",
            }}
          >
            {intl.formatMessage(
              { id: "onboarding.step_of", defaultMessage: "Étape {current}/{total}" },
              { current, total }
            )}
          </Typography>
          {labelId && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontFamily: "'Segoe UI Semibold', system-ui, sans-serif",
                color: "hsl(153, 68%, 25%)",
              }}
            >
              {intl.formatMessage({ id: labelId })}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: "100%",
            height: "6px",
            borderRadius: "9999px",
            bgcolor: "hsl(214.3, 31.8%, 91.4%)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              borderRadius: "9999px",
              bgcolor: "hsl(153, 68%, 25%)",
              transition: "width 0.4s ease",
              width: `${Math.max(percent, 4)}%`,
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {/* Background connector line spanning full width behind circles */}
      <Box
        sx={{
          position: "absolute",
          top: circleSize / 2 - 1,
          left: circleSize / 2,
          right: circleSize / 2,
          height: "2px",
          bgcolor: "hsl(214.3, 31.8%, 91.4%)",
        }}
      />
      {/* Filled portion of the connector */}
      <Box
        sx={{
          position: "absolute",
          top: circleSize / 2 - 1,
          left: circleSize / 2,
          right: circleSize / 2,
          height: "2px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            bgcolor: "hsl(153, 68%, 25%)",
            transition: "width 0.4s ease",
            width:
              activeStep <= 0
                ? "0%"
                : `${(activeStep / (steps.length - 1)) * 100}%`,
          }}
        />
      </Box>

      {/* Step circles + labels */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {steps.map((step, index) => {
          const stepNum = parseInt(step);
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;

          return (
            <Box
              key={step}
              onClick={() => onStepClick && isCompleted && onStepClick(stepNum)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: isCompleted && onStepClick ? "pointer" : "default",
                width: isDesktop ? "80px" : "40px",
              }}
            >
              <Box
                sx={{
                  width: circleSize,
                  height: circleSize,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'Segoe UI Semibold', system-ui, sans-serif",
                  fontSize: isDesktop ? "0.875rem" : "0.75rem",
                  fontWeight: 600,
                  zIndex: 1,
                  transition: "all 0.3s ease",
                  ...(isCompleted
                    ? {
                      bgcolor: "hsl(153, 68%, 25%)",
                      color: "hsl(0, 0%, 100%)",
                    }
                    : isActive
                      ? {
                        bgcolor: "hsl(153, 68%, 25%)",
                        color: "hsl(0, 0%, 100%)",
                      }
                      : {
                        bgcolor: "hsl(214.3, 31.8%, 91.4%)",
                        color: "hsl(215.4, 16.3%, 46.9%)",
                      }),
                }}
              >
                {isCompleted ? (
                  <Check size={isDesktop ? 18 : 14} strokeWidth={3} />
                ) : (
                  index + 1
                )}
              </Box>
              {isDesktop && labels && labels[step] && (
                <Typography
                  sx={{
                    mt: "0.5rem",
                    fontSize: "0.75rem",
                    textAlign: "center",
                    lineHeight: 1.3,
                    fontFamily: isActive
                      ? "'Segoe UI Semibold', system-ui, sans-serif"
                      : "'Segoe UI', system-ui, sans-serif",
                    color: isActive || isCompleted
                      ? "hsl(153, 68%, 25%)"
                      : "hsl(215.4, 16.3%, 46.9%)",
                    transition: "color 0.3s ease",
                  }}
                >
                  {intl.formatMessage({ id: labels[step] })}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProgressStepper;
